/**
 * ROLES CONSTANTS
 * 
 * Define a estrutura de permissões e as colunas da tabela de Cargos.
 */

import { GLOBAL_STATUS, GLOBAL_STATUS_LABELS } from "../../../constants/common";;

export const ROLE_COLUMNS = [
    { key: "name", label: "Nome do Cargo" },
    { key: "description", label: "Descrição" },
    { key: "status", label: "Status", render: (row) => GLOBAL_STATUS_LABELS[row.status] || 'Desconhecido' }, // Usaremos StatusBadge depois
    { key: "updatedAt", label: "Última Atualização" },
];

export const PERMISSION_GROUPS = [
    {
        name: "Dashboards",
        permissions: [
            { id: "dashboards_manager_view", label: "Visualizar Dashboard Gerencial" },
            { id: "dashboards_financial_view", label: "Visualizar Dashboard Financeiro" },
            { id: "dashboards_performance_view", label: "Visualizar Dashboard de Performance" },
        ]
    },
    {
        name: "Performance & Metas",
        permissions: [
            { id: "performance_view", label: "Visualizar Ranking de Performance" },
            { id: "performance_recalculate", label: "Recalcular Performance (mês atual)" },
            { id: "goals_view", label: "Visualizar Metas dos Colaboradores" },
            { id: "goals_manage", label: "Criar e Editar Metas" },
        ]
    },
    {
        name: "Operacional & Atendimento",
        permissions: [
            { id: "members_manage", label: "Acessar e Gerenciar Alunos / Clientes" },
            { id: "grade_manage", label: "Acessar Agenda e Grade Diária" },
            { id: "kiosk_access", label: "Logar no Painel de Quiosque / Totem" },
            { id: "management_evaluation_run", label: "Avaliar Alunos (Aplicar Testes)" },
        ]
    },
    {
        name: "Metodologia & Treinos",
        permissions: [
            { id: "management_training_manage", label: "Criar Biblioteca de Exercícios" },
            { id: "methodology_planning", label: "Criar Planejamento de Aulas / WOD" },
            { id: "admin_activities", label: "Configurar Modalidades" },
            { id: "management_evaluation_levels", label: "Configurar Sistema de Patentes / Níveis" },
            { id: "admin_events", label: "Agendar Exames e Eventos Avaliativos" },
        ]
    },
    {
        name: "Financeiro & Vendas",
        permissions: [
            { id: "financial_cashier", label: "Operar PDV / Receber Pagamentos" },
            { id: "financial_cashflow", label: "Visualizar Relatórios Financeiros (Fluxo/DRE/Vendas)" },
            { id: "financial_config", label: "Configurar Contas Bancárias e Categorias DRE" },
            { id: "financial_acquirers", label: "Configurar Maquininhas e Gateways" },
        ]
    },
    {
        name: "Administrativo & Catálogo",
        permissions: [
            { id: "admin_memberships", label: "Criar e Configurar Planos de Venda" },
            { id: "admin_catalog", label: "Criar e Editar Catálogo de Produtos Físicos" },
            { id: "admin_classes", label: "Criar Turmas e Grade Base" },
            { id: "employees_manage", label: "Gerenciar Equipe e Professores" },
            { id: "admin_portfolio", label: "Acompanhar Carteira de Alunos" },
            { id: "admin_areas", label: "Criar Locais / Espaços Físicos" },
            { id: "admin_roles", label: "Gerenciar Cargos e Permissões" },
            { id: "management_audit_log", label: "Visualizar Logs de Auditoria" },
        ]
    },
    {
        name: "Configurações do Sistema",
        permissions: [
            { id: "settings_company_manage", label: "Alterar Dados da Empresa" },
            { id: "settings_gamification", label: "Configurar Pontuação e Gamificação" },
            { id: "management_automations", label: "Criar Regras de Negócio e Automações" },
            { id: "settings_import", label: "Ferramenta de Importação Massiva" },
            { id: "management_integrations", label: "Gerenciar Integrações Externas" }
        ]
    }
];


/**
 * Retorna os cargos predefinidos para um novo cliente corporativo.
 * Pode ser injetado durante a criação do tenant (Seed) ou na Cloud Function.
 */
export const getDefaultRoles = () => {
    // Helper para gerar um objeto true/false de permissões baseando-se nos IDs
    const buildPermissions = (allowedKeys = 'ALL') => {
        const perms = {};
        PERMISSION_GROUPS.forEach(group => {
            group.permissions.forEach(p => {
                if (allowedKeys === 'ALL') {
                    perms[p.id] = true;
                } else {
                    perms[p.id] = allowedKeys.includes(p.id);
                }
            });
        });
        return perms;
    };

    return [
        {
            name: "Gestor",
            description: "Administrador da unidade. Possui acesso total.",
            status: GLOBAL_STATUS.ACTIVE,
            metricGroup: "gerente",
            permissions: buildPermissions('ALL') // Tudo ligado para o Gestor local
        },
        {
            name: "Coordenador",
            description: "Coordena a parte técnica e treinos, sem acesso financeiro ou de configuração global.",
            status: GLOBAL_STATUS.ACTIVE,
            metricGroup: "gerente",
            permissions: buildPermissions([
                // Dashboards
                'dashboards_manager_view', 'dashboards_performance_view',
                // Performance & Metas
                'performance_view', 'performance_recalculate', 'goals_view', 'goals_manage',
                // Operacional
                'grade_manage', 'members_manage',
                // Metodologia e Treinos
                'methodology_planning', 'management_evaluation_run',
                'admin_activities', 'management_evaluation_levels',
                'admin_events', 'management_training_manage',
                // Financeiro (nada)
                // Administrativo
                'employees_manage', 'admin_classes', 'admin_portfolio', 'admin_catalog'
                // Configurações (nada)
            ])
        },
        {
            name: "Professor",
            description: "Pode ver turmas, alunos e realizar testes e avaliações.",
            status: GLOBAL_STATUS.ACTIVE,
            metricGroup: "professor",
            permissions: buildPermissions([
                'grade_manage', 'members_manage',
                'management_evaluation_run', 'management_training_manage',
                'kiosk_access'
            ])
        },
        {
            name: "Recepcionista",
            description: "Foca no atendimento ao cliente, caixa, vendas e CRM.",
            status: GLOBAL_STATUS.ACTIVE,
            metricGroup: "consultor",
            permissions: buildPermissions([
                'grade_manage', 'members_manage', 'kiosk_access',
                'financial_cashier', 'admin_memberships'
            ])
        },
        {
            name: "Totem (Kiosk)",
            description: "Acesso bloqueado ao ERP. Redireciona imediatamente para o Quiosque de Autoatendimento.",
            status: GLOBAL_STATUS.ACTIVE,
            metricGroup: "",
            permissions: buildPermissions(['kiosk_access'])
        }
    ];
};
